import type { ReactElement } from 'react';
import { Icon } from './ui/Icon.js';
import type { RegionFidelity, RegionMechanism } from '../types/index.js';

/**
 * Spec 171 — region honesty labels.
 *
 * The spine carried from capture (168) → analysis (170) → UI (171): label what
 * we actually did. An API-set region is NOT a real in-country logged-in user, so
 * a region-specific metric must say so. This reuses the 154 API-vs-consumer
 * caveat tone — plain language, no jargon, sits beside the 177–179 reliability
 * tier. NEVER surfaces a "spec-NNN" reference to the end user.
 */

/** Plain-language gloss for each fidelity level (no jargon, end-user facing). */
const FIDELITY_COPY: Record<RegionFidelity, { label: string; title: string; tone: string }> = {
  replicated: {
    label: 'Region applied',
    title: 'The region was truly applied to this engine call — closest to what a local user sees.',
    tone: 'bg-green-100 text-green-700',
  },
  approximated: {
    label: 'Region approximated',
    title: 'The region was hinted to the model via an API location parameter — not a real in-country, logged-in user, so it can differ from what a local person sees.',
    tone: 'bg-amber-100 text-amber-700',
  },
  indicative_only: {
    label: 'Region not controlled',
    title: 'This engine cannot be pinned to a region — the result is indicative only, not what a local user is guaranteed to see.',
    tone: 'bg-slate-100 text-slate-600',
  },
};

/** Plain-language gloss for the mechanism actually used. */
const MECHANISM_COPY: Record<RegionMechanism, string> = {
  api_location_param: 'via API location hint',
  proxy_egress: 'via in-country connection',
  dataforseo_location: 'via localized search',
  prompt_stated: 'region stated in the prompt',
  none: 'no region control',
};

/**
 * Resolve the effective fidelity/mechanism: engine-level wins, else region-level.
 *
 * Spec 244 - every parameter and both results are nullable. The region-level
 * arguments were typed non-nullable while their source (`RegionVisibility`) is
 * populated straight from a nullable column, so `??` could and did fall through
 * to null. Returning `RegionFidelity` from a function that can return null is
 * how the crash reached a component that trusted the type.
 */
export function effectiveRegion(
  regionFidelity: RegionFidelity | null | undefined,
  regionMechanism: RegionMechanism | null | undefined,
  engineFidelity?: RegionFidelity | null,
  engineMechanism?: RegionMechanism | null,
): { fidelity: RegionFidelity | null; mechanism: RegionMechanism | null } {
  return {
    fidelity: engineFidelity ?? regionFidelity ?? null,
    mechanism: engineMechanism ?? regionMechanism ?? null,
  };
}

interface RegionFidelityLabelProps {
  /**
   * Spec 244 — nullable, because the API sends null and always could.
   *
   * This was typed `RegionFidelity` (non-nullable) and the component indexed
   * FIDELITY_COPY with it unguarded. TypeScript was satisfied and the page
   * still crashed: region_source_overlap returns `region_fidelity: null` for a
   * region with no cited domains, and `effectiveRegion` returns
   * `engineFidelity ?? regionFidelity` — null when both are. The type described
   * a guarantee the runtime never made.
   */
  fidelity: RegionFidelity | null | undefined;
  mechanism: RegionMechanism | null | undefined;
  /** Hide the mechanism gloss in tight layouts (the badge alone still carries the title). */
  showMechanism?: boolean;
}

/**
 * Small label naming how faithfully a region was reproduced for a given metric.
 * Renders beside the reliability tier so a client never reads an API-set result
 * as "exactly what a Danish user sees."
 */
export function RegionFidelityLabel({
  fidelity,
  mechanism,
  showMechanism = true,
}: RegionFidelityLabelProps): ReactElement | null {
  // Spec 244 — render NOTHING rather than crash the page.
  //
  // An unguarded FIDELITY_COPY[fidelity] threw "Cannot read properties of
  // undefined (reading 'title')" and took the whole Overview down through the
  // error boundary. ResponseRegionBadge, three functions below, already had the
  // guard; this one never did, and the difference only showed on a brand with
  // TWO monitored regions, because a single-region brand does not render a
  // per-region fidelity label at all. The first two-region brand this install
  // ever held found it immediately — no fixture had one, so 859 gates did not.
  //
  // Absent fidelity means "we do not know how faithfully the region was
  // reproduced". Saying nothing is the honest rendering of that; inventing a
  // tone or defaulting to `replicated` would claim a guarantee nobody measured.
  const copy = fidelity ? FIDELITY_COPY[fidelity] : undefined;
  if (!copy) return null;
  return (
    <span
      data-testid="region-fidelity-label"
      title={copy.title}
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium ${copy.tone}`}
    >
      <Icon name="bx-info-circle" className="opacity-70" />
      {copy.label}
      {showMechanism && mechanism && mechanism !== 'none' && (
        <span className="font-normal opacity-70">· {MECHANISM_COPY[mechanism]}</span>
      )}
    </span>
  );
}

/**
 * Spec 171 — partial-coverage marker. Where a region is `indicative_only` for an
 * engine (gemini/mistral/ungrounded chatgpt cannot be pinned to a location) the
 * dashboard labels it "Partial coverage" — a visible, honest gap, never a silent
 * omission and never overstated. Renders nothing for replicated/approximated.
 */
export function RegionPartialCoverage({
  fidelity,
}: {
  /** Spec 244 - nullable for the same reason as RegionFidelityLabel's. */
  fidelity: RegionFidelity | null | undefined;
}): ReactElement | null {
  if (fidelity !== 'indicative_only') return null;
  return (
    <span
      data-testid="region-partial-coverage"
      title="This engine can't be pinned to a region — its result is shown as indicative coverage of this region, not a region-specific measurement."
      className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500"
    >
      Partial coverage
    </span>
  );
}

interface ResponseRegionBadgeProps {
  regionCode?: string | null;
  mechanism?: RegionMechanism | null;
  fidelity?: RegionFidelity | null;
}

/**
 * Region badge for a captured response — sits beside the 154 CaptureModeBadge in
 * the Response Browser. Shows the region code + (when known) its fidelity tone +
 * mechanism gloss. Renders nothing when the response carries no region_code
 * (pre-region rows / single-region brands with no region capture).
 */
export function ResponseRegionBadge({
  regionCode,
  mechanism,
  fidelity,
}: ResponseRegionBadgeProps): ReactElement | null {
  if (!regionCode) return null;
  const tone = fidelity ? FIDELITY_COPY[fidelity].tone : 'bg-slate-100 text-slate-600';
  // `none` is the absence of a mechanism — the fidelity label already says
  // "Region not controlled", so the gloss would just restate it. Suppress it.
  const mech = mechanism && mechanism !== 'none' ? MECHANISM_COPY[mechanism] : null;
  const fidelityLabel = fidelity ? FIDELITY_COPY[fidelity].label : null;
  const title = [
    `Region: ${regionCode}`,
    fidelity ? FIDELITY_COPY[fidelity].title : null,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <span
      data-testid="response-region-badge"
      title={title}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      <Icon name="bx-globe" className="opacity-70" />
      {regionCode}
      {fidelityLabel && <span className="font-normal opacity-70">· {fidelityLabel}</span>}
      {mech && <span className="font-normal opacity-60">· {mech}</span>}
    </span>
  );
}
